/* eslint @typescript-eslint/explicit-module-boundary-types: "off" */
import React, { Component } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RefreshIcon from "@material-ui/icons/Refresh";

import * as cognitoAdmin from "./cognito_admin";

const AGENCIES = ["CQA", "GVB", "Health", "NDS"];
const ROLES = ["ADMIN", "AGENT"];

class ListUsers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      loading: true,
      error: null,
      // Add user dialog
      addDialogOpen: false,
      addFormData: {
        username: "",
        email: "",
        givenName: "",
        familyName: "",
        agency: "",
        role: "",
      },
      addFormErrors: {},
      addFormSubmitting: false,
      // Delete user dialog
      deleteDialogOpen: false,
      userToDelete: null,
      deleteSubmitting: false,
      // Disable/Enable user
      statusChanging: null,
    };
  }

  componentDidMount() {
    this.loadUsers();
  }

  loadUsers = async () => {
    this.setState({ loading: true, error: null });
    try {
      const response = await cognitoAdmin.listUsers();
      this.setState({ users: response.Users || [], loading: false });
    } catch (err) {
      console.error("Error loading users:", err);
      this.setState({
        error:
          "Unable to load users, likely because you are not in the UserAdmins group.",
        loading: false,
      });
    }
  };

  // Helper to get attribute value from Cognito user
  getUserAttribute = (user, attrName) => {
    const attr = user.Attributes?.find((a) => a.Name === attrName);
    return attr?.Value || "";
  };

  // Add User Dialog Handlers
  handleOpenAddDialog = () => {
    this.setState({
      addDialogOpen: true,
      addFormData: {
        username: "",
        email: "",
        givenName: "",
        familyName: "",
        agency: "",
        role: "",
      },
      addFormErrors: {},
    });
  };

  handleCloseAddDialog = () => {
    this.setState({ addDialogOpen: false });
  };

  handleAddFormChange = (field) => (event) => {
    this.setState({
      addFormData: {
        ...this.state.addFormData,
        [field]: event.target.value,
      },
      addFormErrors: {
        ...this.state.addFormErrors,
        [field]: false,
      },
    });
  };

  validateAddForm = () => {
    const { username, email, givenName, familyName, agency, role } =
      this.state.addFormData;
    const errors = {};
    if (!username) errors.username = true;
    if (!email || !email.includes("@")) errors.email = true;
    if (!givenName) errors.givenName = true;
    if (!familyName) errors.familyName = true;
    if (!agency) errors.agency = true;
    if (!role) errors.role = true;
    this.setState({ addFormErrors: errors });
    return Object.keys(errors).length === 0;
  };

  handleAddUser = async () => {
    if (!this.validateAddForm()) return;

    this.setState({ addFormSubmitting: true });
    const { username, email, givenName, familyName, agency, role } =
      this.state.addFormData;

    try {
      await cognitoAdmin.createUser(
        username,
        email,
        givenName,
        familyName,
        agency,
        role
      );
      this.setState({ addDialogOpen: false, addFormSubmitting: false });
      this.loadUsers();
    } catch (err) {
      console.error("Error creating user:", err);
      this.setState({
        addFormErrors: { submit: err.message || "Failed to create user" },
        addFormSubmitting: false,
      });
    }
  };

  // Delete User Dialog Handlers
  handleOpenDeleteDialog = (user) => {
    this.setState({ deleteDialogOpen: true, userToDelete: user });
  };

  handleCloseDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false, userToDelete: null });
  };

  handleDeleteUser = async () => {
    const { userToDelete } = this.state;
    if (!userToDelete) return;

    this.setState({ deleteSubmitting: true });
    try {
      await cognitoAdmin.deleteUser(userToDelete.Username);
      this.setState({
        deleteDialogOpen: false,
        userToDelete: null,
        deleteSubmitting: false,
      });
      this.loadUsers();
    } catch (err) {
      console.error("Error deleting user:", err);
      this.setState({ deleteSubmitting: false });
      alert("Failed to delete user: " + (err.message || "Unknown error"));
    }
  };

  // Enable/Disable User Handlers
  handleToggleUserStatus = async (user) => {
    const isEnabled = user.Enabled;
    this.setState({ statusChanging: user.Username });

    try {
      if (isEnabled) {
        await cognitoAdmin.disableUser(user.Username);
      } else {
        await cognitoAdmin.enableUser(user.Username);
      }
      this.loadUsers();
    } catch (err) {
      console.error("Error changing user status:", err);
      alert(
        "Failed to change user status: " + (err.message || "Unknown error")
      );
    } finally {
      this.setState({ statusChanging: null });
    }
  };

  render() {
    const {
      users,
      loading,
      error,
      addDialogOpen,
      addFormData,
      addFormErrors,
      addFormSubmitting,
      deleteDialogOpen,
      userToDelete,
      deleteSubmitting,
      statusChanging,
    } = this.state;

    return (
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Typography variant="h5">User Management</Typography>
              <div>
                <IconButton
                  onClick={this.loadUsers}
                  disabled={loading}
                  title="Refresh"
                >
                  <RefreshIcon />
                </IconButton>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.handleOpenAddDialog}
                >
                  Add User
                </Button>
              </div>
            </div>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 16 }}
            >
              Manage Cognito users. Add new users (they will receive an email
              invitation), enable/disable user accounts, or permanently delete
              users.
            </Typography>

            {loading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 32,
                }}
              >
                <CircularProgress />
              </div>
            )}

            {error && (
              <Typography color="error" style={{ padding: 16 }}>
                {error}
              </Typography>
            )}

            {!loading && !error && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Agency</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                    {users.map((user) => (
                      <TableRow key={user.Username}>
                        <TableCell>{user.Username}</TableCell>
                        <TableCell>
                          {this.getUserAttribute(user, "email") ||
                            user.Username}
                        </TableCell>
                        <TableCell>
                          {this.getUserAttribute(user, "given_name")}{" "}
                          {this.getUserAttribute(user, "family_name")}
                        </TableCell>
                        <TableCell>
                          {this.getUserAttribute(user, "custom:agency")}
                        </TableCell>
                        <TableCell>
                          {(
                            this.getUserAttribute(user, "custom:role") ||
                            "AGENT"
                          ).toUpperCase()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.Enabled ? "Active" : "Disabled"}
                            color={user.Enabled ? "primary" : "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const isAdmin =
                              (
                                this.getUserAttribute(user, "custom:role") ||
                                "AGENT"
                              ).toUpperCase() === "ADMIN";
                            return (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    this.handleToggleUserStatus(user)
                                  }
                                  disabled={
                                    isAdmin || statusChanging === user.Username
                                  }
                                  title={
                                    isAdmin
                                      ? "Cannot disable an admin user"
                                      : user.Enabled
                                      ? "Disable User"
                                      : "Enable User"
                                  }
                                >
                                  {statusChanging === user.Username ? (
                                    <CircularProgress size={20} />
                                  ) : user.Enabled ? (
                                    <BlockIcon />
                                  ) : (
                                    <CheckCircleIcon />
                                  )}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    this.handleOpenDeleteDialog(user)
                                  }
                                  disabled={isAdmin}
                                  title={
                                    isAdmin
                                      ? "Cannot delete an admin user"
                                      : "Delete User"
                                  }
                                  style={{
                                    color: isAdmin ? undefined : "#f44336",
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add User Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={this.handleCloseAddDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the user details below. They will receive an email
              invitation with a temporary password.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Username"
              type="text"
              fullWidth
              value={addFormData.username}
              onChange={this.handleAddFormChange("username")}
              error={addFormErrors.username}
              helperText={addFormErrors.username ? "Username is required" : ""}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={addFormData.email}
              onChange={this.handleAddFormChange("email")}
              error={addFormErrors.email}
              helperText={addFormErrors.email ? "Valid email is required" : ""}
            />
            <TextField
              margin="dense"
              label="Given Name"
              type="text"
              fullWidth
              value={addFormData.givenName}
              onChange={this.handleAddFormChange("givenName")}
              error={addFormErrors.givenName}
              helperText={
                addFormErrors.givenName ? "Given name is required" : ""
              }
            />
            <TextField
              margin="dense"
              label="Family Name"
              type="text"
              fullWidth
              value={addFormData.familyName}
              onChange={this.handleAddFormChange("familyName")}
              error={addFormErrors.familyName}
              helperText={
                addFormErrors.familyName ? "Family name is required" : ""
              }
            />
            <FormControl fullWidth margin="dense" error={addFormErrors.agency}>
              <InputLabel>Agency</InputLabel>
              <Select
                value={addFormData.agency}
                onChange={this.handleAddFormChange("agency")}
              >
                {AGENCIES.map((agency) => (
                  <MenuItem key={agency} value={agency}>
                    {agency}
                  </MenuItem>
                ))}
              </Select>
              {addFormErrors.agency && (
                <FormHelperText>Agency is required</FormHelperText>
              )}
            </FormControl>
            <FormControl fullWidth margin="dense" error={addFormErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={addFormData.role}
                onChange={this.handleAddFormChange("role")}
              >
                {ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
              {addFormErrors.role && (
                <FormHelperText>Role is required</FormHelperText>
              )}
            </FormControl>
            {addFormErrors.submit && (
              <Typography color="error" style={{ marginTop: 16 }}>
                {addFormErrors.submit}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleCloseAddDialog}
              disabled={addFormSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleAddUser}
              color="primary"
              variant="contained"
              disabled={addFormSubmitting}
            >
              {addFormSubmitting ? <CircularProgress size={24} /> : "Add User"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={this.handleCloseDeleteDialog}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to permanently delete the user{" "}
              <strong>
                {userToDelete
                  ? `${userToDelete.Username} (${this.getUserAttribute(
                      userToDelete,
                      "email"
                    )})`
                  : ""}
              </strong>
              ?
              <br />
              <br />
              <strong style={{ color: "#f44336" }}>
                This action cannot be undone.
              </strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleCloseDeleteDialog}
              disabled={deleteSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleDeleteUser}
              style={{ backgroundColor: "#f44336", color: "white" }}
              variant="contained"
              disabled={deleteSubmitting}
            >
              {deleteSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                "Delete User"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    );
  }
}

export default ListUsers;
